import {z} from 'zod';

// Helper schemas for common validation patterns
const hexColorSchema = z
  .string()
  .regex(
    /^#?[0-9A-Fa-f]{6}$/,
    'Must be a valid hex color (e.g., #ffffff or ffffff)'
  )
  .or(z.literal(''));

const imageFormatSchema = z.enum(['', 'jpeg', 'jpg', 'png', 'webp'], {
  errorMap: () => ({message: 'Must be jpeg, jpg, png, or webp'})
});

const qualitySchema = z.coerce
  .number({
    invalid_type_error: 'Must be a number'
  })
  .int('Must be a whole number')
  .min(1, 'Quality must be at least 1')
  .max(100, 'Quality must be at most 100')
  .nullable();

const percentageSchema = z.coerce
  .number({
    invalid_type_error: 'Must be a number'
  })
  .min(0, 'Must be at least 0')
  .max(1, 'Must be at most 1 (use decimal format, e.g., 0.8 for 80%)')
  .nullable();

const binaryIntegerSchema = z.coerce
  .number({
    invalid_type_error: 'Must be a number'
  })
  .int('Must be a whole number')
  .min(0, 'Must be 0 or 1')
  .max(1, 'Must be 0 or 1');

// Design CID pattern: e.g., djn0-00by-wu36
const designCidSchema = z
  .string()
  .trim()
  .regex(/^[a-z0-9]{4}-[a-z0-9]{4}-[a-z0-9]{4}$/i, {
    message: 'Must be a valid Design CID (e.g., djn0-00by-wu36)'
  })
  .or(z.literal(''));

// Color ID pattern: numeric string (e.g., 176100)
const colorIdSchema = z
  .string()
  .trim()
  .regex(/^\d+$/, {
    message: 'Must be a numeric color ID (e.g., 176100)'
  })
  .or(z.literal(''))
  .or(z.null());

// Main form schema
export const algoliaStyleSchema = z
  .object({
    assetHash: z.any().optional(), // Hidden field, no validation
    autoNegate: binaryIntegerSchema,
    colorId: colorIdSchema,
    debopacity: percentageSchema.optional(),
    design: designCidSchema,

    // Optional fields
    digest: z.any().optional(), // Hidden field, no validation
    imdeboss: z.boolean(),
    // Advanced rendering effects
    imengrave: z.boolean(),
    // Image processing parameters (nested object)
    ixbg: hexColorSchema,
    ixfm: imageFormatSchema,
    ixh: z.any().optional(), // Hidden field, no validation
    ixq: qualitySchema,
    ixw: z.any().optional(), // Hidden field, no validation
    noauto: z.boolean(),
    opacity: percentageSchema.optional(),

    // Placement settings
    placeMax: binaryIntegerSchema,
    placeMaxCenter: percentageSchema,
    placeMaxPct: percentageSchema,
    placeUseProduct: binaryIntegerSchema,
    placeUseView: z
      .enum(['', 'front', 'back', 'left', 'right', 'top', 'bottom'])
      .optional(),

    // Core settings
    useLabImage: z.boolean()
  })
  .refine(
    (data) => {
      // When not using lab image, skip deboss validation entirely
      if (!data.useLabImage) return true;
      // If debossing is enabled, deboss opacity should be set
      if (data.imdeboss) {
        return (
          data.debopacity !== null &&
          data.debopacity !== undefined &&
          typeof data.debopacity === 'number'
        );
      }
      return true;
    },
    {
      message: 'Deboss opacity is required when debossing is enabled',
      path: ['debopacity']
    }
  )
  .refine((data) => !data.useLabImage || !(data.imengrave && data.imdeboss), {
    message: 'Engraving and Debossing cannot both be enabled',
    path: ['imdeboss']
  })
  .refine(
    (data) => {
      if (!data.useLabImage) return true;
      // Require non-empty valid Design CID when using lab image
      return (
        data.design !== '' && designCidSchema.safeParse(data.design).success
      );
    },
    {
      message: 'Design CID is required when using lab image',
      path: ['design']
    }
  )
  .refine(
    (data) => {
      // When not using lab image, skip engrave validation entirely
      if (!data.useLabImage) return true;
      // If engraving is enabled, opacity should be set
      if (data.imengrave) {
        return (
          data.opacity !== null &&
          data.opacity !== undefined &&
          typeof data.opacity === 'number'
        );
      }
      return true;
    },
    {
      message: 'Opacity is required when engraving is enabled',
      path: ['opacity']
    }
  );

export default algoliaStyleSchema;
